import React from "react";
import { motion } from "framer-motion";
import IPhoneMockup from "@/components/IPhoneMockup";
import { APP_SCREENS, APP_SCREENS_ALT } from "@/lib/assets";

/**
 * RestaurantPreviewSection — shows the 2 real Feaststar app screenshots
 * (restaurant header + restaurant details) side-by-side in iPhone frames.
 *
 * Reused across PartnerLanding (audience: restaurant owners) and customer
 * landing pages (HomeLanding, CityStyleLanding) with audience-appropriate copy.
 *
 * @param {Object} props
 * @param {string} props.eyebrow - small uppercase label above the heading
 * @param {string} props.headingA - first part of H2 (regular weight)
 * @param {string} props.headingB - second part of H2 (italic)
 * @param {string} props.description - paragraph below the heading
 * @param {string} [props.testId="restaurant-preview"]
 */
export default function RestaurantPreviewSection({
  eyebrow,
  headingA,
  headingB,
  description,
  testId = "restaurant-preview",
}) {
  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section
      className="section-pad-sm relative overflow-hidden"
      data-testid={testId}
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Copy column */}
        <div className="lg:col-span-5">
          {eyebrow && (
            <motion.div {...fade(0)} className="section-label" data-testid={`${testId}-eyebrow`}>
              {eyebrow}
            </motion.div>
          )}
          <motion.h2 className="display-lg mt-3" {...fade(0.06)} data-testid={`${testId}-heading`}>
            <span className="block">{headingA}</span>
            {headingB && <span className="block italic">{headingB}</span>}
          </motion.h2>
          {description && (
            <motion.p
              className="body-lg mt-6 whitespace-pre-line max-w-xl"
              {...fade(0.12)}
              data-testid={`${testId}-description`}
            >
              {description}
            </motion.p>
          )}
        </div>

        {/* iPhones column — 2 phones side-by-side on the same baseline */}
        <div className="lg:col-span-7 relative">
          <div className="flex justify-center lg:justify-end items-center gap-4 sm:gap-6 lg:gap-8">
            <motion.div
              {...fade(0.16)}
              className="relative"
              data-testid={`${testId}-phone-1`}
            >
              <IPhoneMockup
                width={220}
                image={APP_SCREENS.restaurantHeader}
                alt={APP_SCREENS_ALT.restaurantHeader}
                testId={`${testId}-iphone-header`}
              />
            </motion.div>

            <motion.div
              {...fade(0.26)}
              className="relative"
              data-testid={`${testId}-phone-2`}
            >
              <IPhoneMockup
                width={220}
                image={APP_SCREENS.restaurantDetails}
                alt={APP_SCREENS_ALT.restaurantDetails}
                testId={`${testId}-iphone-details`}
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
